import type { ProductPageContent } from "./types";
import { TRACELOCK_APP } from "../nav/navigation";

/**
 * Framework and connector counts are taken from the TraceLock crosswalk
 * (`src/server/compliance/crosswalk.ts`) and the registered connector set.
 * The crosswalk's own provenance note is reflected in `methodology`: the
 * AI-governance mappings are curated at clause level and reviewed before they
 * are used as an audit deliverable, so this page does not claim otherwise.
 */
export const TRACELOCK: ProductPageContent = {
  slug: "tracelock",
  proofClaim:
    "One control evaluation, reported against every framework you are held to — with evidence an auditor can verify rather than take on trust.",
  lede:
    "TraceLock is continuous control monitoring and evidence management. Connectors test your live environment, findings map through one unified control library, and every artifact lands in an append-only record with its own hash.",
  ctas: [
    { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
    { label: "See pricing", href: "#editions" },
  ],
  problem: {
    heading: "Audit readiness decays the moment the screenshot is taken",
    body:
      "Point-in-time evidence describes a system that no longer exists, and every new framework means re-testing controls you already tested. The work is duplicated, the evidence is stale, and nobody can prove when it was collected.",
    points: [
      {
        title: "Evidence goes stale",
        body: "A folder of screenshots proves what was true on the day someone had time to collect them, not what is true now.",
      },
      {
        title: "Every framework re-tests the same control",
        body: "Access review, logging and change management get evaluated once per framework instead of once, mapped many.",
      },
      {
        title: "Nobody can verify the chain",
        body: "If evidence can be edited after the fact, the auditor is trusting your word rather than checking a record.",
      },
    ],
  },
  capabilities: {
    heading: "Evaluate once, report many",
    items: [
      {
        title: "Unified Control Library with a cross-framework crosswalk",
        body: "Internal control codes map to SOC 2, ISO 27001, NIST CSF 2.0, NIST AI RMF, ISO/IEC 42001, the EU AI Act, HIPAA, SEC rules and CIS Controls v8, with fifteen regulated framework packs available on top. One evaluation populates every report it defensibly maps to.",
      },
      {
        title: "Live connectors instead of questionnaires",
        body: "AWS, GCP, GitHub, Cloudflare and custody connectors test the real environment on a schedule and raise findings against the controls they cover.",
      },
      {
        title: "Append-only evidence with hashes",
        body: "Every artifact is recorded write-once with a SHA-256 digest, so an auditor can confirm nothing changed after collection.",
      },
      {
        title: "A read-only room for your auditor",
        body: "Scoped, revocable access to exactly the evidence in scope — no shared drives, no mailbox attachments, no standing accounts.",
      },
      {
        title: "AI inventory and governance",
        body: "Discover the models and AI services in use, attach them to obligations, and keep the EU AI Act and ISO 42001 mappings current as the estate changes.",
      },
      {
        title: "A Privacy Wall in front of connector data",
        body: "Connectors return posture and findings, not payloads. What TraceLock stores is deliberately narrower than what it can see.",
      },
    ],
  },
  evidence: {
    heading: "What is actually covered",
    lede:
      "Numbers you can check against the product rather than a maturity claim.",
    stats: [
      {
        value: "9",
        label: "frameworks in the core crosswalk",
        note: "SOC 2 · ISO 27001 · NIST CSF 2.0 · NIST AI RMF 1.0 · ISO/IEC 42001:2023 · EU AI Act (2024/1689) · HIPAA Security Rule · SEC rules · CIS Controls v8.",
      },
      {
        value: "15",
        label: "regulated framework packs",
        note: "Attestation packs for financial-services, AML, sanctions and digital-asset regimes, sold per framework. Listed below.",
      },
      {
        value: "Write-once",
        label: "evidence records",
        note: "Append-only with per-artifact SHA-256 digests and recorded custody.",
      },
      {
        value: "Live",
        label: "control testing",
        note: "Cloud, code, custody and log-feed connectors run against the real environment on a schedule.",
      },
    ],
    coverage: [
      {
        heading: "Core frameworks — every plan",
        items: [
          "SOC 2 (TSC 2017)",
          "ISO/IEC 27001",
          "NIST Cybersecurity Framework 2.0",
          "HIPAA Security Rule",
          "SEC Investment Adviser Rules",
          "CIS Controls v8",
        ],
        note: "Tested continuously by connectors against the live environment.",
      },
      {
        heading: "AI governance — Growth and Enterprise",
        items: ["NIST AI Risk Management Framework 1.0", "ISO/IEC 42001:2023", "EU AI Act (2024/1689)"],
        note: "Mapped at article, clause and function level; see the methodology note.",
      },
      {
        heading: "Regulated framework packs — financial services",
        items: [
          "NYDFS Part 500 Cybersecurity Requirements",
          "SEC and FINRA books-and-records retention (17a-4)",
          "Bank Secrecy Act record retention",
          "UK FCA financial promotions regime",
          "UCC Articles 8 and 12 — securities and controllable electronic records",
        ],
      },
      {
        heading: "Regulated framework packs — AML and sanctions",
        items: [
          "OFAC sanctions compliance",
          "FinCEN money services business obligations",
          "FATF Travel Rule and third-party reliance (R.16/17)",
          "EU Anti-Money Laundering Regulation record-keeping",
          "EU Transfer of Funds Regulation",
          "UK Money Laundering Regulations 2017",
        ],
      },
      {
        heading: "Regulated framework packs — digital assets",
        items: [
          "FATF virtual asset service provider guidance",
          "EU Markets in Crypto-Assets Regulation (MiCA)",
          "NYDFS Part 200 virtual currency business activity (BitLicense)",
          "US payment stablecoin requirements (GENIUS Act)",
        ],
        note: "Each pack is one purchasable framework; Enterprise includes an allowance of two.",
      },
    ],
    methodology:
      "EU AI Act, ISO 42001 and NIST AI RMF references are curated at article, clause and function level rather than sub-control, and are intended for a compliance reviewer to confirm before they are relied on as an audit deliverable. Where no defensible mapping exists the field is left empty rather than filled in. EU AI Act citations use the final adopted 2024 numbering. Regulated framework pack controls are attested — an owner records the review, the evidence and the date, and TraceLock tracks staleness against a 90-, 180- or 365-day interval. TraceLock does not screen transactions, exchange Travel Rule messages, determine regulatory status or verify reserves.",
  },
  /**
   * Prices and limits are the live ones TraceLock itself charges and enforces:
   * the Stripe prices configured in its App Engine deployment, and the plan
   * entitlements in `src/server/billing/entitlements.ts`. Publishing a number
   * here that its checkout does not charge is the failure mode, so these move
   * only when that deployment does.
   */
  editions: {
    heading: "Plans",
    lede:
      "Priced per organization and billed by TraceLock. Every plan includes the core crosswalk, append-only evidence and the Privacy Wall.",
    items: [
      {
        name: "Assurance Attach",
        price: "$499/month or $5,988/year",
        blurb:
          "SOC 2 and ISO 27001 monitoring for teams whose other regulatory program runs elsewhere.",
        bullets: [
          "SOC 2 and ISO 27001 only",
          "3 connectors",
          "20 monitored users",
          "One read-only auditor room",
        ],
        cta: { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
      },
      {
        name: "Starter",
        price: "$1,299/month or $12,990/year",
        blurb: "Continuous monitoring for a first audit, on a single environment.",
        bullets: [
          "3 connectors",
          "20 monitored users",
          "One read-only auditor room",
          "Unified Control Library across the six core frameworks",
          "Append-only evidence with SHA-256 digests",
        ],
        cta: { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
      },
      {
        name: "Growth",
        price: "$2,999/month or $29,990/year",
        blurb: "For a live program: more estate, custody posture, AI governance.",
        bullets: [
          "10 connectors and 100 monitored users",
          "Three read-only auditor rooms",
          "Custody posture and user access reviews",
          "EU AI Act and ISO 42001 AI governance",
          "Third-party risk and a custom trust-center domain",
        ],
        cta: { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
      },
      {
        name: "Enterprise",
        price: "$90,000/year",
        blurb: "Multi-entity scope, auditor rooms, SSO and two regulated framework packs.",
        bullets: [
          "100 connectors and 500 monitored users",
          "Unlimited read-only auditor rooms",
          "SAML single sign-on",
          "Allowance of two regulated framework packs",
          "Everything in Growth",
        ],
        cta: { label: "Request a briefing", href: "/contact?topic=tracelock" },
      },
    ],
    /** Same source as the plans: `COMMERCIAL_ADD_ONS` in TraceLock's billing catalog. */
    addOns: {
      heading: "Add-ons",
      lede: "Added to any plan from TraceLock's billing settings.",
      items: [
        {
          name: "Regulated framework pack",
          price: "$9,000/year per framework",
          note: "One of the fifteen regulated frameworks above: its control set, attestation workflow, evidence and staleness tracking. The framework is chosen at checkout.",
        },
        {
          name: "Extra connector pack",
          price: "$99/month",
          note: "Three more connectors on top of your plan's allowance.",
        },
        {
          name: "Extra auditor room",
          price: "$299/month",
          note: "One more scoped, expiring read-only room for an external auditor.",
        },
        {
          name: "Monitored-user overage",
          price: "$3/user/month",
          note: "Each monitored identity beyond the plan's limit.",
        },
        {
          name: "Premium support",
          price: "$500/month",
          note: "Priority response from the TraceLock team through your audit window.",
        },
        {
          name: "White-glove onboarding",
          price: "$2,500 one-time",
          note: "Guided setup: connectors, control owners and your first evidence cycle, run with you.",
        },
      ],
    },
  },
  services: {
    heading: "Run it as a managed program",
    body:
      "Warden builds the crosswalk out to your control set, operates evidence collection, and manages the auditor room through the examination.",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
      { label: "Auditor Room Management", href: "/warden/auditor-room" },
      { label: "EU AI Act Readiness", href: "/warden/eu-ai-act" },
    ],
  },
  resources: {
    heading: "Go deeper",
    items: [
      { kind: "Solution", label: "Stay Continuously Audit-Ready", href: "/audit-ready" },
      { kind: "Solution", label: "Prove AI Compliance", href: "/ai-compliance" },
      { kind: "Trust", label: "Odingard Trust Center", href: "https://trust.odingard.com", external: true },
      { kind: "Industry", label: "Financial Services", href: "/industry/financial-services" },
    ],
  },
  close: {
    heading: "Stop rebuilding the same evidence pack",
    body:
      "See the control library and connectors against your own environment, or have Warden run the program.",
    ctas: [
      { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
      { label: "Request a briefing", href: "/contact?topic=tracelock" },
    ],
  },
};
